import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Typography,
  FormControl,
  FormControlLabel,
  Switch,
  TextField,
  Button,
  CircularProgress,
  Alert,
  MenuItem,
  Select,
  InputLabel,
} from '@mui/material';
import { AuthContext } from '../context/AuthContext';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const daysOfWeek = [
  { id: 1, name: 'Dimanche' },
  { id: 2, name: 'Lundi' },
  { id: 3, name: 'Mardi' },
  { id: 4, name: 'Mercredi' },
  { id: 5, name: 'Jeudi' },
  { id: 6, name: 'Vendredi' },
  { id: 7, name: 'Samedi' },
];

const HorairesSection = ({ restaurantId }) => {
  const { user, loading: authLoading } = useContext(AuthContext);
  const [horaires, setHoraires] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [newDayId, setNewDayId] = useState(''); // For adding new horaire
  const navigate = useNavigate();

  // Fetch horaires on mount
  useEffect(() => {
    const fetchHoraires = async () => {
      console.log('HorairesSection - User:', user, 'AuthLoading:', authLoading);
      console.log('HorairesSection - User properties:', Object.keys(user || {}));
      if (authLoading) {
        console.log('HorairesSection - Waiting for auth to complete');
        return;
      }
      const token = localStorage.getItem('token');
      console.log('HorairesSection - Token from localStorage:', token);
      if (!token) {
        console.log('HorairesSection - No token found, redirecting to login');
        setError('Utilisateur non authentifié. Veuillez vous connecter.');
        setLoading(false);
        navigate('/login');
        return;
      }
      if (!user || !['admin', 'manager'].includes(user.role)) {
        console.log('HorairesSection - Invalid user or role, redirecting to login');
        setError('Accès non autorisé.');
        setLoading(false);
        navigate('/login');
        return;
      }
      try {
        const response = await axios.get(`https://apibackoffice.lalivraison.ma/api/restaurants/${restaurantId}/horaires`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        console.log('HorairesSection - API response:', response.data);
        setHoraires(response.data);
        setLoading(false);
      } catch (err) {
        console.error('HorairesSection - Fetch error details:', {
          status: err.response?.status,
          data: err.response?.data,
          message: err.message,
        });
        setError(err.response?.data?.message || 'Erreur lors du chargement des horaires.');
        setLoading(false);
      }
    };
    fetchHoraires();
  }, [restaurantId, user, authLoading, navigate]);

  // Handle input changes for a specific day
  const handleInputChange = (dayId, field, value) => {
    setHoraires((prev) =>
      prev.map((horaire) =>
        horaire.id_jour === dayId ? { ...horaire, [field]: value } : horaire
      )
    );
  };

  // Handle switch toggle for closed days with immediate API update
  const handleFermeToggle = async (dayId) => {
    const token = localStorage.getItem('token');
    if (!token) {
      setError('Utilisateur non authentifié. Veuillez vous connecter.');
      navigate('/login');
      return;
    }

    // Update local state first
    let updatedHoraire = null;
    setHoraires((prev) => {
      const updatedHoraires = prev.map((horaire) => {
        if (horaire.id_jour === dayId) {
          const newFerme = horaire.ferme ? 0 : 1;
          updatedHoraire = {
            ...horaire,
            ferme: newFerme,
            heure_ouverture: newFerme ? null : '08:00:00',
            heure_fermeture: newFerme ? null : '22:00:00',
            heure_fermeture_pause: newFerme ? null : null,
            heure_ouverture_pause: newFerme ? null : null,
          };
          return updatedHoraire;
        }
        return horaire;
      });
      console.log('HorairesSection - After ferme toggle for id_jour:', dayId, 'Updated horaire:', updatedHoraire);
      return updatedHoraires;
    });

    // Immediately update backend
    try {
      const response = await axios.put(
        `https://apibackoffice.lalivraison.ma/api/restaurants/${restaurantId}/horaires/${dayId}`,
        updatedHoraire,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log('HorairesSection - Ferme toggle response:', response.data);
      setSuccess(`Jour ${daysOfWeek.find((day) => day.id === dayId)?.name} mis à jour.`);
      setError(null);
    } catch (err) {
      console.error('HorairesSection - Ferme toggle error details:', {
        status: err.response?.status,
        data: err.response?.data,
        message: err.message,
      });
      setError(err.response?.data?.message || 'Erreur lors de la mise à jour du jour.');
      // Revert state on failure
      setHoraires((prev) =>
        prev.map((horaire) =>
          horaire.id_jour === dayId ? { ...horaire, ferme: horaire.ferme ? 1 : 0 } : horaire
        )
      );
    }
  };

  // Add new horaire for a day
  const handleAddHoraire = async () => {
    if (!newDayId) {
      setError('Veuillez sélectionner un jour.');
      return;
    }
    const token = localStorage.getItem('token');
    if (!token) {
      setError('Utilisateur non authentifié. Veuillez vous connecter.');
      navigate('/login');
      return;
    }
    // Check if horaire already exists for the day
    if (horaires.some((h) => h.id_jour === parseInt(newDayId))) {
      setError('Un horaire existe déjà pour ce jour.');
      return;
    }
    const newHoraire = {
      id_jour: parseInt(newDayId),
      heure_ouverture: '08:00:00',
      heure_fermeture: '22:00:00',
      heure_fermeture_pause: null,
      heure_ouverture_pause: null,
      ferme: 0,
    };
    try {
      const response = await axios.post(
        `https://apibackoffice.lalivraison.ma/api/restaurants/${restaurantId}/horaires`,
        newHoraire,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log('HorairesSection - Add response:', response.data);
      // Refetch horaires to ensure consistency
      const fetchResponse = await axios.get(`https://apibackoffice.lalivraison.ma/api/restaurants/${restaurantId}/horaires`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setHoraires(fetchResponse.data);
      setSuccess('Horaire ajouté avec succès.');
      setNewDayId('');
      setError(null);
    } catch (err) {
      console.error('HorairesSection - Add error details:', {
        status: err.response?.status,
        data: err.response?.data,
        message: err.message,
      });
      setError(err.response?.data?.message || 'Erreur lors de l’ajout de l’horaire.');
    }
  };

  // Delete horaire for a day
  const handleDeleteHoraire = async (dayId) => {
    const token = localStorage.getItem('token');
    if (!token) {
      setError('Utilisateur non authentifié. Veuillez vous connecter.');
      navigate('/login');
      return;
    }
    console.log('HorairesSection - Deleting horaire for id_jour:', dayId);
    try {
      const response = await axios.delete(
        `https://apibackoffice.lalivraison.ma/api/restaurants/${restaurantId}/horaires/${dayId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log('HorairesSection - Delete response:', response.data);
      // Remove the deleted horaire from state
      setHoraires((prev) => prev.filter((horaire) => horaire.id_jour !== dayId));
      setSuccess('Horaire supprimé avec succès.');
      setError(null);
    } catch (err) {
      console.error('HorairesSection - Delete error details:', {
        status: err.response?.status,
        data: err.response?.data,
        message: err.message,
      });
      setError(err.response?.data?.message || 'Erreur lors de la suppression de l’horaire.');
    }
  };

  // Save (update) all horaires
  const handleSave = async () => {
    const token = localStorage.getItem('token');
    console.log('HorairesSection - Saving horaires with token:', token);
    console.log('HorairesSection - Horaires to save:', horaires);
    if (!token) {
      console.log('HorairesSection - No token found for save, redirecting to login');
      setError('Utilisateur non authentifié. Veuillez vous connecter.');
      navigate('/login');
      return;
    }
    if (!user || !['admin', 'manager'].includes(user.role)) {
      console.log('HorairesSection - Invalid user or role for save, redirecting to login');
      setError('Accès non autorisé.');
      navigate('/login');
      return;
    }
    if (horaires.length === 0) {
      setError('Aucun horaire à enregistrer.');
      return;
    }
    setError(null);
    setSuccess(null);
    try {
      const response = await axios.put(
        `https://apibackoffice.lalivraison.ma/api/restaurants/${restaurantId}/horaires`,
        { horaires },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log('HorairesSection - Save response:', response.data);
      // Refetch horaires to ensure UI reflects backend state
      const fetchResponse = await axios.get(`https://apibackoffice.lalivraison.ma/api/restaurants/${restaurantId}/horaires`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setHoraires(fetchResponse.data);
      setSuccess('Horaires mis à jour avec succès.');
    } catch (err) {
      console.error('HorairesSection - Save error details:', {
        status: err.response?.status,
        data: err.response?.data,
        message: err.message,
      });
      setError(err.response?.data?.message || 'Erreur lors de la mise à jour des horaires.');
    }
  };

  if (loading || authLoading) {
    console.log('HorairesSection - Showing loading spinner');
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Get available days for adding new horaires
  const availableDays = daysOfWeek.filter((day) => !horaires.some((h) => h.id_jour === day.id));

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#1e293b' }}>
        Horaires d'Ouverture
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}
      {/* Add new horaire */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel id="new-day-select-label">Ajouter un jour</InputLabel>
          <Select
            labelId="new-day-select-label"
            value={newDayId}
            label="Ajouter un jour"
            onChange={(e) => setNewDayId(e.target.value)}
          >
            {availableDays.map((day) => (
              <MenuItem key={day.id} value={day.id}>
                {day.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          variant="contained"
          onClick={handleAddHoraire}
          disabled={!newDayId}
          sx={{
            bgcolor: '#21ccbb',
            '&:hover': { bgcolor: '#1ab8a9' }, // Slightly darker shade for hover
            textTransform: 'none',
            fontWeight: 600,
          }}
        >
          Ajouter
        </Button>
      </Box>
      {/* Display existing horaires */}
      {horaires.length === 0 ? (
        <Typography>Aucun horaire défini. Ajoutez un jour ci-dessus.</Typography>
      ) : (
        horaires.map((horaire) => (
          <Box
            key={horaire.id_jour}
            sx={{
              mb: 3,
              p: 2,
              border: '1px solid #21ccbb', // Updated border color
              borderRadius: 2,
              bgcolor: '#fff',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Typography sx={{ flex: 1, fontWeight: 500 }}>
                {daysOfWeek.find((day) => day.id === horaire.id_jour)?.name || `Jour ${horaire.id_jour}`}
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={!!horaire.ferme}
                    onChange={() => handleFermeToggle(horaire.id_jour)}
                    color="primary"
                  />
                }
                label="Fermé"
              />
              <Button
                variant="outlined"
                color="error"
                onClick={() => handleDeleteHoraire(horaire.id_jour)}
                sx={{ ml: 2, textTransform: 'none' }}
              >
                Supprimer
              </Button>
            </Box>
            {!horaire.ferme && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <FormControl sx={{ minWidth: 120 }}>
                  <TextField
                    label="Ouverture"
                    type="time"
                    value={horaire.heure_ouverture?.slice(0, 5) || '08:00'}
                    onChange={(e) =>
                      handleInputChange(horaire.id_jour, 'heure_ouverture', `${e.target.value}:00`)
                    }
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ step: 300 }}
                  />
                </FormControl>
                <FormControl sx={{ minWidth: 120 }}>
                  <TextField
                    label="Début Pause"
                    type="time"
                    value={horaire.heure_fermeture_pause?.slice(0, 5) || ''}
                    onChange={(e) =>
                      handleInputChange(
                        horaire.id_jour,
                        'heure_fermeture_pause',
                        e.target.value ? `${e.target.value}:00` : null
                      )
                    }
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ step: 300 }}
                  />
                </FormControl>
                <FormControl sx={{ minWidth: 120 }}>
                  <TextField
                    label="Fin Pause"
                    type="time"
                    value={horaire.heure_ouverture_pause?.slice(0, 5) || ''}
                    onChange={(e) =>
                      handleInputChange(
                        horaire.id_jour,
                        'heure_ouverture_pause',
                        e.target.value ? `${e.target.value}:00` : null
                      )
                    }
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ step: 300 }}
                  />
                </FormControl>
                <FormControl sx={{ minWidth: 120 }}>
                  <TextField
                    label="Fermeture"
                    type="time"
                    value={horaire.heure_fermeture?.slice(0, 5) || '22:00'}
                    onChange={(e) =>
                      handleInputChange(horaire.id_jour, 'heure_fermeture', `${e.target.value}:00`)
                    }
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ step: 300 }}
                  />
                </FormControl>
              </Box>
            )}
          </Box>
        ))
      )}
      {horaires.length > 0 && (
        <Button
          variant="contained"
          onClick={handleSave}
          sx={{
            mt: 2,
            bgcolor: '#21ccbb',
            '&:hover': { bgcolor: '#1ab8a9' }, // Slightly darker shade for hover
            textTransform: 'none',
            fontWeight: 600,
          }}
        >
          Enregistrer les Horaires
        </Button>
      )}
    </Box>
  );
};

export default HorairesSection;